"use client";

import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Heart, DollarSign, CreditCard, Check, ShieldCheck, User, Mail, Phone, MessageSquare, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
const SHARED_DATA = {
  business_name: "COHCC Foundation",
  address: "1234 Hispanic Community Ave, Denver, CO 80202",
  email: "info@cohccfoundation.org"
};
const getImpactNote = (amount: number, isRecurring: boolean) => {
  const frequency = isRecurring ? "every month" : "";
  const val = Number(amount) || 0;
  if (val >= 500) return `Your $${val} gift ${frequency} sponsors a complete community leadership workshop series.`;
  if (val >= 100) return `Your $${val} gift ${frequency} provides 5 hours of dedicated business coaching for an entrepreneur.`;
  if (val >= 50) return `Your $${val} gift ${frequency} supplies essential educational materials for a youth program student.`;
  if (val >= 25) return `Your $${val} gift ${frequency} helps cover registration fees for networking events.`;
  return `Your contribution of $${val > 0 ? `$${val}` : "any amount"} ${frequency} directly supports our mission to expand economic opportunity.`;
};
function DonationFormContent() {
  const {
    control,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm
  } = useWvcForm();
  const {
    setValue
  } = useFormContext();
  const amount = useWatch({
    control,
    name: "amount"
  });
  const recurring = useWatch({
    control,
    name: "recurring"
  });
  const handlePresetClick = (value: number) => {
    setValue("amount", value, {
      shouldValidate: true,
      shouldDirty: true
    });
  };
  const handleTabChange = (value: string) => {
    setValue("recurring", value === "monthly", {
      shouldValidate: true,
      shouldDirty: true
    });
  };
  const currentTab = recurring ? "monthly" : "one-time";
  const impactNote = getImpactNote(amount, !!recurring);
  if (isSubmitted && !submitError) {
    return <Card className="w-full max-w-xl mx-auto border-none shadow-xl bg-white/95 backdrop-blur-sm animate-in fade-in zoom-in duration-500">
        <CardContent className="pt-12 pb-12 flex flex-col items-center text-center space-y-6">
          <div className="size-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Check className="size-10 text-green-600" />
          </div>
          <h3 className="text-3xl font-serif font-bold text-[var(--color-primary)]">Thank You!</h3>
          <p className="text-lg text-[var(--color-text-dark)] max-w-md">
            {successMessage || "Your generous donation has been received. We appreciate your support in advancing prosperity for our community."}
          </p>
          <Button onClick={resetForm} variant="outline" className="mt-6 border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white">
            Make Another Donation
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="w-full max-w-2xl mx-auto border-none shadow-2xl bg-white overflow-hidden">
      <CardHeader className="bg-[var(--color-primary)] text-white text-center py-8 px-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1214114/img/pattern_geometric.png)] bg-repeat"></div>
        <div className="relative z-10">
          <CardTitle className="text-3xl md:text-4xl font-serif font-bold mb-2">Invest in Impact</CardTitle>
          <CardDescription className="text-blue-100 text-lg">
            Securely support {SHARED_DATA.business_name}'s mission today.
          </CardDescription>
        </div>
      </CardHeader>

      <CardContent className="p-6 md:p-8 space-y-8">
        <Form onSubmit={handleSubmit} className="space-y-8">
          
          {}
          <div className="space-y-6 bg-slate-50 p-6 rounded-xl border border-slate-100">
            <Tabs value={currentTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="grid w-full grid-cols-2 h-12 bg-slate-200/50 p-1">
                <TabsTrigger value="one-time" className="text-base data-[state=active]:bg-white data-[state=active]:text-[var(--color-primary)] data-[state=active]:shadow-sm font-medium">
                  One-Time Gift
                </TabsTrigger>
                <TabsTrigger value="monthly" className="text-base data-[state=active]:bg-white data-[state=active]:text-[var(--color-primary)] data-[state=active]:shadow-sm font-medium">
                  Monthly Recurring
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {}
            <div className="hidden">
              <FormField control={control} name="recurring" render={({
              field
            }) => <FormItem>
                    <FormControl>
                      <Input type="checkbox" checked={field.value} onChange={field.onChange} value={field.value ? "true" : "false"} />
                    </FormControl>
                  </FormItem>} />
            </div>

            <div className="space-y-4">
              <FormLabel className="text-base font-semibold text-[var(--color-primary)]">Select Amount</FormLabel>
              <div className="grid grid-cols-3 gap-3">
                {[50, 100, 500].map(preset => <Button key={preset} type="button" variant={Number(amount) === preset ? "default" : "outline"} onClick={() => handlePresetClick(preset)} className={cn("h-14 text-lg font-bold transition-all hover:scale-105", Number(amount) === preset ? "bg-[var(--color-secondary)] text-[var(--color-primary)] border-[var(--color-secondary)] hover:bg-[var(--color-secondary)]/90" : "border-slate-200 text-slate-600 hover:border-[var(--color-secondary)] hover:text-[var(--color-primary)]")}>
                    ${preset}
                  </Button>)}
              </div>

              <FormField control={control} name="amount" rules={{
              required: "Please enter a donation amount",
              min: {
                value: 5,
                message: "Minimum donation is $5"
              }
            }} render={({
              field
            }) => <FormItem className="relative">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <DollarSign className="h-5 w-5 text-slate-400" />
                      </div>
                      <FormControl>
                        <Input type="number" placeholder="Other Amount" className="pl-10 h-14 text-lg bg-white border-slate-200 focus-visible:ring-[var(--color-secondary)]" {...field} onChange={e => field.onChange(e.target.valueAsNumber)} />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>} />

              {}
              <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-4 flex gap-3 items-start animate-in fade-in slide-in-from-top-2 duration-300">
                <Heart className="h-5 w-5 text-[var(--color-secondary)] shrink-0 mt-0.5 fill-[var(--color-secondary)]" />
                <p className="text-sm text-[var(--color-primary)] font-medium leading-relaxed">
                  {impactNote}
                </p>
              </div>
            </div>
          </div>

          {}
          <div className="space-y-4">
            <h4 className="text-lg font-serif font-bold text-[var(--color-primary)] border-b border-slate-100 pb-2 mb-4">
              Your Information
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField control={control} name="name" rules={{
              required: "Full Name is required"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <User className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                        <Input placeholder="Jane Doe" className="pl-9" {...field} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={control} name="email" rules={{
              required: "Email is required",
              pattern: {
                value: /^\S+@\S+$/i,
                message: "Invalid email address"
              }
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                        <Input type="email" placeholder="jane@example.com" className="pl-9" {...field} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={control} name="phone" render={({
              field
            }) => <FormItem>
                    <FormLabel>Phone Number <span className="text-slate-400 font-normal">(Optional)</span></FormLabel>
                    <FormControl>
                        <div className="relative">
                        <Phone className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                        <Input type="tel" placeholder="(555) 123-4567" className="pl-9" {...field} />
                        </div>
                    </FormControl>
                    <FormMessage />
                    </FormItem>} />
            </div>

            <FormField control={control} name="message" render={({
            field
          }) => <FormItem>
                  <FormLabel>Message <span className="text-slate-400 font-normal">(Optional)</span></FormLabel>
                  <FormControl>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <Input className="pl-9 min-h-[80px] py-2" placeholder="Tell us why you're investing in impact..." {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm flex items-center gap-2">
              <span className="font-bold">Error:</span> {submitError}
            </div>}

          <div className="pt-2">
            <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90 text-white shadow-lg hover:shadow-xl transition-all cursor-pointer">
              {isSubmitting ? <span className="flex items-center gap-2">
                  <Loader className="animate-spin h-5 w-5" />
                  Processing...
                </span> : <span className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  {recurring ? `Donate $${Number(amount) || 0} Monthly` : `Donate $${Number(amount) || 0} Now`}
                </span>}
            </Button>
            <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4 text-green-600" />
              <span>Secure 256-bit SSL Encrypted Payment</span>
            </div>
          </div>

        </Form>
      </CardContent>
    </Card>;
}
export default function InvestInImpact() {
  return <section id="invest-in-impact" className="py-24 bg-slate-50 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-64 bg-[var(--color-primary)]/5 skew-y-3 origin-top-left transform -translate-y-20 pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[var(--color-secondary)]/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-[var(--color-primary)] mb-4">
            Make a Lasting Difference
          </h2>
          <p className="text-lg text-[var(--color-text-dark)] leading-relaxed">
            Your investment empowers Hispanic entrepreneurs, leaders, and youth across Colorado. 
            Join us in building a legacy of prosperity and opportunity.
          </p>
        </div>

        <FormProvider formId="investment_form_get_involved" sectionName="Invest in Impact" defaultValues={{
        name: "",
        email: "",
        phone: "",
        amount: 100,
        recurring: false,
        message: ""
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        amount: {
          required: true,
          min: 5
        }
      }} submitText="Donate Now" successMessage="Thank you for your generous investment in our community's future!">
          <DonationFormContent />
        </FormProvider>
      </div>
    </section>;
}